import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { getDiario, getMayor, getPUC } from '../services/api';

// Lleva contenido (toolbar / conteo) a un destino de la cabecera por id.
function Portal({ id, children }) {
  const [el, setEl] = useState(null);
  useEffect(() => { setEl(document.getElementById(id)); return () => setEl(null); }, [id]);
  return el ? createPortal(children, el) : null;
}

const fmtCOP = n => Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hoy = () => new Date().toISOString().slice(0, 10);
const ene1 = () => `${new Date().getFullYear()}-01-01`;

export default function LibrosContables() {
  const [tab, setTab] = useState('diario');
  return (
    <div style={S.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <p style={S.sub}>Consultas oficiales — solo comprobantes contabilizados</p>
        <span id="libros-count" style={S.hint} />
      </div>
      <div style={{ ...S.tabs, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={tab === 'diario' ? S.tabActive : S.tab} onClick={() => setTab('diario')}>Libro Diario</button>
          <button style={tab === 'mayor'  ? S.tabActive : S.tab} onClick={() => setTab('mayor')}>Libro Mayor</button>
        </div>
        <div id="libros-toolbar" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }} />
      </div>
      {tab === 'diario' ? <Diario /> : <Mayor />}
    </div>
  );
}

// ── Libro Diario ─────────────────────────────────────────────────────────────
function Diario() {
  const [filtro, setFiltro] = useState({ fecha_desde: ene1(), fecha_hasta: hoy() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cargar = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await getDiario(filtro);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar diario');
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); /* eslint-disable-line */ }, []);

  return (
    <div>
      <Portal id="libros-toolbar">
        <label style={S.lbl}>Desde</label>
        <input type="date" value={filtro.fecha_desde} onChange={e => setFiltro(f => ({ ...f, fecha_desde: e.target.value }))} style={S.input} />
        <label style={S.lbl}>Hasta</label>
        <input type="date" value={filtro.fecha_hasta} onChange={e => setFiltro(f => ({ ...f, fecha_hasta: e.target.value }))} style={S.input} />
        <button onClick={cargar} style={S.btnPri}>Generar</button>
      </Portal>
      {data && <Portal id="libros-count">{data.total_comprobantes} comprobantes</Portal>}
      {error && <div style={S.error}>{error}</div>}
      {loading && <div style={S.loading}>Cargando…</div>}
      {data && !loading && (
        <>
          {data.comprobantes.map(c => (
            <div key={c.id} style={S.compCard}>
              <div style={S.compHead}>
                <div>
                  <span style={S.compTipo}>{c.tipo}</span>
                  <span style={S.compNum}>{c.numero}</span>
                  <span style={S.compFecha}>{c.fecha}</span>
                </div>
                <div style={{ color: '#64748b', fontSize: 13 }}>{c.descripcion || ''}</div>
              </div>
              <table style={S.miniTable}>
                <thead>
                  <tr>
                    <th style={S.miniTh}>Cuenta</th>
                    <th style={{ ...S.miniTh, textAlign: 'left' }}>Detalle</th>
                    <th style={{ ...S.miniTh, textAlign: 'right' }}>Débito</th>
                    <th style={{ ...S.miniTh, textAlign: 'right' }}>Crédito</th>
                  </tr>
                </thead>
                <tbody>
                  {c.asientos.map((a, i) => (
                    <tr key={i}>
                      <td style={{ ...S.miniTd, fontFamily: 'monospace' }}>{a.cuenta_codigo}</td>
                      <td style={S.miniTd}>{a.cuenta_nombre}{a.descripcion ? ` — ${a.descripcion}` : ''}</td>
                      <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{a.debito  > 0 ? fmtCOP(a.debito)  : ''}</td>
                      <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{a.credito > 0 ? fmtCOP(a.credito) : ''}</td>
                    </tr>
                  ))}
                  <tr style={{ background: '#f1f5f9', fontWeight: 600 }}>
                    <td colSpan={2} style={S.miniTd}>Totales</td>
                    <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(c.total_debito)}</td>
                    <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(c.total_credito)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
          {data.comprobantes.length === 0 && (
            <div style={S.empty}>Sin comprobantes contabilizados en el rango</div>
          )}
          {data.comprobantes.length > 0 && (
            <div style={S.totalGral}>
              <span>Total general:</span>
              <span style={{ fontFamily: 'monospace' }}>
                Débitos {fmtCOP(data.total_debito)} · Créditos {fmtCOP(data.total_credito)}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Libro Mayor (por cuenta) ─────────────────────────────────────────────────
function Mayor() {
  const [cuentas, setCuentas] = useState([]);
  const [filtro, setFiltro]   = useState({ cuenta: '', fecha_desde: ene1(), fecha_hasta: hoy() });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await getPUC({ activa: true });
      setCuentas(data.filter(c => c.acepta_movimiento));
    })();
  }, []);

  const cargar = async () => {
    if (!filtro.cuenta) { setError('Selecciona una cuenta'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await getMayor(filtro);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar mayor');
    } finally { setLoading(false); }
  };

  const opciones = useMemo(() => cuentas.map(c => (
    <option key={c.codigo} value={c.codigo}>{c.codigo} — {c.nombre}</option>
  )), [cuentas]);

  return (
    <div>
      <Portal id="libros-toolbar">
        <label style={S.lbl}>Cuenta</label>
        <select value={filtro.cuenta} onChange={e => setFiltro(f => ({ ...f, cuenta: e.target.value }))} style={{ ...S.input, minWidth: 260 }}>
          <option value="">— selecciona una cuenta —</option>
          {opciones}
        </select>
        <label style={S.lbl}>Desde</label>
        <input type="date" value={filtro.fecha_desde} onChange={e => setFiltro(f => ({ ...f, fecha_desde: e.target.value }))} style={S.input} />
        <label style={S.lbl}>Hasta</label>
        <input type="date" value={filtro.fecha_hasta} onChange={e => setFiltro(f => ({ ...f, fecha_hasta: e.target.value }))} style={S.input} />
        <button onClick={cargar} style={S.btnPri} disabled={!filtro.cuenta}>Generar</button>
      </Portal>
      {error && <div style={S.error}>{error}</div>}
      {loading && <div style={S.loading}>Cargando…</div>}
      {data && !loading && (
        <div style={S.compCard}>
          <div style={S.compHead}>
            <div>
              <span style={S.compNum}>{data.cuenta.codigo}</span>
              <span style={{ fontWeight: 600, marginLeft: 12 }}>{data.cuenta.nombre}</span>
              <span style={{ ...S.badge, marginLeft: 12 }}>
                Naturaleza {data.cuenta.naturaleza === 'D' ? 'Débito' : 'Crédito'}
              </span>
            </div>
            <div style={{ fontFamily: 'monospace', fontWeight: 600, color: data.saldo_final >= 0 ? '#15803d' : '#b91c1c' }}>
              Saldo final: {fmtCOP(data.saldo_final)}
            </div>
          </div>

          <div style={{ padding: '8px 12px', background: '#f8fafc', fontSize: 13, color: '#475569' }}>
            Saldo inicial al {data.fecha_desde}: <span style={{ fontFamily: 'monospace' }}>{fmtCOP(data.saldo_inicial)}</span>
          </div>

          <table style={S.miniTable}>
            <thead>
              <tr>
                <th style={S.miniTh}>Fecha</th>
                <th style={S.miniTh}>Comprobante</th>
                <th style={{ ...S.miniTh, textAlign: 'left' }}>Detalle</th>
                <th style={{ ...S.miniTh, textAlign: 'right' }}>Débito</th>
                <th style={{ ...S.miniTh, textAlign: 'right' }}>Crédito</th>
                <th style={{ ...S.miniTh, textAlign: 'right' }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {data.lineas.map((l, i) => (
                <tr key={i}>
                  <td style={S.miniTd}>{l.fecha}</td>
                  <td style={{ ...S.miniTd, fontFamily: 'monospace', fontSize: 12 }}>{l.tipo} {l.numero}</td>
                  <td style={S.miniTd}>{l.descripcion || l.comprobante_desc || ''}</td>
                  <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{l.debito  > 0 ? fmtCOP(l.debito)  : ''}</td>
                  <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{l.credito > 0 ? fmtCOP(l.credito) : ''}</td>
                  <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(l.saldo)}</td>
                </tr>
              ))}
              {data.lineas.length === 0 && (
                <tr><td colSpan={6} style={{ ...S.miniTd, textAlign: 'center', color: '#94a3b8', padding: 28 }}>Sin movimientos en el rango</td></tr>
              )}
              {data.lineas.length > 0 && (
                <tr style={{ background: '#f1f5f9', fontWeight: 600 }}>
                  <td colSpan={3} style={S.miniTd}>Totales del periodo</td>
                  <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(data.total_debito)}</td>
                  <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(data.total_credito)}</td>
                  <td style={{ ...S.miniTd, textAlign: 'right', fontFamily: 'monospace' }}>{fmtCOP(data.saldo_final)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S = {
  container: { padding: 24, maxWidth: 1300, margin: '0 auto' },
  header:    { marginBottom: 16 },
  title:     { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  sub:       { color: '#64748b', margin: '4px 0 0', fontSize: 13 },
  tabs:      { display: 'flex', gap: 8, borderBottom: '1px solid #334155', marginBottom: 16, paddingBottom: 4 },
  tab:       { padding: '8px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: '#94a3b8', borderBottom: '2px solid transparent', fontWeight: 500 },
  tabActive: { padding: '8px 16px', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14, color: '#f1f5f9', borderBottom: '2px solid #3b82f6', fontWeight: 600 },
  toolbar:   { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' },
  lbl:       { fontSize: 12, color: '#64748b', fontWeight: 500 },
  input:     { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' },
  hint:      { color: '#64748b', fontSize: 13, marginLeft: 'auto' },
  btnPri:    { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  error:     { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  loading:   { padding: 40, textAlign: 'center', color: '#64748b' },
  empty:     { padding: 40, textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  compCard:  { background: '#fff', borderRadius: 8, marginBottom: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' },
  compHead:  { padding: '10px 14px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  compTipo:  { background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, marginRight: 8 },
  compNum:   { fontFamily: 'monospace', fontWeight: 600, color: '#0f172a' },
  compFecha: { color: '#64748b', fontSize: 13, marginLeft: 12 },
  badge:     { background: '#e0e7ff', color: '#3730a3', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 },
  miniTable: { width: '100%', borderCollapse: 'collapse' },
  miniTh:    { background: '#f8fafc', padding: '6px 10px', fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase', textAlign: 'right', borderBottom: '1px solid #e2e8f0' },
  miniTd:    { padding: '4px 10px', fontSize: 13, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
  totalGral: { display: 'flex', justifyContent: 'space-between', padding: '12px 18px', background: '#0f172a', color: '#fff', borderRadius: 8, marginTop: 8, fontWeight: 600 },
};
